import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, doc, getDocs, query, updateDoc, where, Timestamp } from 'firebase/firestore';
import { useListasConfig } from './useListasConfig';
import { useParchear } from './DatosContext';
import DialogoConfirmar from './Dialogo';
import { ejecutarEliminacion } from './operaciones';

// ─── Ficha de oportunidad (sesión 34) ─────────────────────────────────────────
// Una sola ficha para todo METRON (módulo Oportunidades, ficha del lead y Dashboard). Se abre en modo lectura,
// como las fichas de lead, presupuesto, cliente y proyecto; para cambiar algo hay que pulsar Editar.
//
// Estado (regla del estado agregado, sesiones 30 y 32): si la oportunidad tiene presupuestos, su estado sale de
// ellos y no se toca a mano. Sin presupuestos solo puede estar Pendiente o Descartada, y eso sí lo elige el usuario.

export const ESTADOS_OP = {
  pendiente:     { etiqueta: 'Pendiente',     color: '#534AB7' },
  presupuestado: { etiqueta: 'Presupuestado', color: '#BA7517' },
  aceptado:      { etiqueta: 'Aceptado',      color: '#1D9E75' },
  rechazado:     { etiqueta: 'Rechazado',     color: '#D85A30' },
  descartado:    { etiqueta: 'Descartado',    color: '#6b7280' },
};
const ESTADOS_PRES = {
  enviado:   { etiqueta: 'Enviado',   color: '#534AB7' },
  aceptado:  { etiqueta: 'Aceptado',  color: '#1D9E75' },
  rechazado: { etiqueta: 'Rechazado', color: '#D85A30' },
};
// Estados que se eligen a mano (oportunidad sin presupuestos). Úsalos también en los formularios de alta.
export const OPCIONES_ESTADO_MANUAL = [
  { valor: 'pendiente', etiqueta: 'Pendiente' },
  { valor: 'descartado', etiqueta: 'Descartado' },
];
export const estadoEditableOp = (estado) => !estado || estado === 'pendiente' || estado === 'descartado';

const aFecha = (v) => (v?.toDate ? v.toDate() : v instanceof Date ? v : v ? new Date(v) : null);
const fechaInput = (v) => {
  const d = aFecha(v);
  if (!d || isNaN(d)) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const fechaLarga = (v) => {
  const d = aFecha(v);
  return d && !isNaN(d) ? d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
};
const fmtEuros = (n) => (n ? new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0, useGrouping: 'always' }).format(n) : '—');
const fmtM2 = (n) => (n ? `${new Intl.NumberFormat('es-ES', { maximumFractionDigits: 0, useGrouping: 'always' }).format(n)} m²` : '—');

// Campos editables, normalizados para poder comparar (¿hay cambios de verdad?)
const formDesde = (op) => ({
  fecha_entrada: fechaInput(op.fecha_entrada),
  estado: op.estado || 'pendiente',
  sector: op.sector || '',
  superficie: op.superficie ? String(op.superficie) : '',
  presupuesto_estimado: op.presupuesto_estimado ? String(op.presupuesto_estimado) : '',
  direccion: op.direccion || '', cp: op.cp || '', ciudad: op.ciudad || '',
  provincia: op.provincia || '', pais: op.pais || '', observaciones: op.observaciones || '',
});

function Badge({ estado, tabla }) {
  const e = tabla[estado] || { etiqueta: estado || '—', color: '#534AB7' };
  return <span style={{ background: e.color, color: '#fff', fontSize: '0.72rem', fontWeight: 500, padding: '3px 10px', borderRadius: '20px', whiteSpace: 'nowrap' }}>{e.etiqueta}</span>;
}

function Dato({ label, children }) {
  return (
    <div style={S.fila}>
      <span style={S.etiqueta}>{label}</span>
      <span style={S.valor}>{children}</span>
    </div>
  );
}

function Campo({ label, children, ancho }) {
  return (
    <div style={{ gridColumn: ancho ? '1 / -1' : 'auto', display: 'flex', flexDirection: 'column', gap: '6px' }}>
      <label style={S.labelCampo}>{label}</label>
      {children}
    </div>
  );
}

/**
 * oportunidad: { id, leadId, leadNombre, leadEmpresa, ...campos }
 * onVolver(): salir de la ficha. textoVolver: texto del enlace de vuelta.
 * onActualizada(op): tras guardar. onEliminada(id): tras eliminar. onNavegar: para abrir presupuestos y el lead.
 */
export default function FichaOportunidad({ oportunidad, esAdmin, onVolver, textoVolver = 'Oportunidades', onActualizada, onEliminada, onNavegar, mostrarLead = true }) {
  const { sectores } = useListasConfig(['sectores']);
  const parchearOps = useParchear('oportunidades');
  const [datos, setDatos] = useState(oportunidad);
  const [editando, setEditando] = useState(false);
  const [form, setForm] = useState(() => formDesde(oportunidad));
  const [presupuestos, setPresupuestos] = useState(null); // null = cargando
  const [dialogo, setDialogo] = useState(null); // 'salir' | 'cancelar' | 'eliminar'
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const r = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', r);
    return () => window.removeEventListener('resize', r);
  }, []);

  // Presupuestos vinculados: fuente única del vínculo = presupuesto.oportunidadId (sesión 30). Pocas lecturas.
  useEffect(() => {
    let vivo = true;
    getDocs(query(collection(db, 'presupuestos'), where('oportunidadId', '==', oportunidad.id)))
      .then(snap => { if (vivo) setPresupuestos(snap.docs.map(d => ({ id: d.id, ...d.data() }))); })
      .catch(e => { console.error('No se pudieron leer los presupuestos de la oportunidad', e); if (vivo) setPresupuestos([]); });
    return () => { vivo = false; };
  }, [oportunidad.id]);

  const tienePres = (presupuestos || []).length > 0;
  const estadoManual = estadoEditableOp(datos.estado) && !tienePres;
  const hayCambios = editando && JSON.stringify(form) !== JSON.stringify(formDesde(datos));
  const set = (campo, valor) => setForm(f => ({ ...f, [campo]: valor }));

  const empezarEdicion = () => { setForm(formDesde(datos)); setError(''); setEditando(true); };
  const cancelar = () => (hayCambios ? setDialogo('cancelar') : setEditando(false));
  const volver = () => (hayCambios ? setDialogo('salir') : onVolver());

  const guardar = async () => {
    if (!form.fecha_entrada) { setError('La fecha de contacto es obligatoria.'); return; }
    setGuardando(true); setError('');
    const cambios = {
      fecha_entrada: Timestamp.fromDate(new Date(`${form.fecha_entrada}T12:00:00`)),
      sector: form.sector,
      superficie: form.superficie ? parseFloat(form.superficie) : null,
      presupuesto_estimado: form.presupuesto_estimado ? parseFloat(form.presupuesto_estimado) : null,
      direccion: form.direccion, cp: form.cp, ciudad: form.ciudad, provincia: form.provincia, pais: form.pais,
      observaciones: form.observaciones,
      // El estado solo se escribe si se elige a mano (sin presupuestos)
      ...(estadoManual ? { estado: form.estado === 'descartado' ? 'descartado' : 'pendiente' } : {}),
    };
    try {
      await updateDoc(doc(db, 'leads', datos.leadId, 'posibles_proyectos', datos.id), cambios);
      const nueva = { ...datos, ...cambios };
      setDatos(nueva);
      parchearOps(prev => prev.map(o => (o.id === nueva.id ? { ...o, ...cambios } : o)));
      if (onActualizada) onActualizada(nueva);
      setEditando(false);
    } catch (e) { console.error(e); setError('No se pudo guardar. Inténtalo de nuevo.'); }
    setGuardando(false);
  };

  const eliminar = async () => {
    setGuardando(true);
    try {
      await ejecutarEliminacion('oportunidad', datos.id, datos);
      parchearOps(prev => prev.filter(o => o.id !== datos.id));
      setDialogo(null);
      if (onEliminada) onEliminada(datos.id);
    } catch (e) {
      console.error(e);
      setDialogo(null);
      setError(e.message || 'No se pudo eliminar la oportunidad.');
    }
    setGuardando(false);
  };

  const pad = isMobile ? '16px' : '32px';
  const lugar = [datos.direccion, datos.cp, datos.ciudad, datos.provincia, datos.pais].filter(Boolean).join(', ');

  return (
    <div style={{ paddingBottom: editando ? '100px' : '32px' }}>
      {dialogo === 'salir' && <DialogoConfirmar titulo="Cambios sin guardar" mensaje="¿Quieres salir sin guardar los cambios de esta oportunidad?"
        textoSeguro="Seguir editando" textoPeligroso="Salir sin guardar" onSeguro={() => setDialogo(null)} onPeligroso={() => { setDialogo(null); onVolver(); }} />}
      {dialogo === 'cancelar' && <DialogoConfirmar titulo="Cambios sin guardar" mensaje="¿Descartar los cambios que has hecho en esta oportunidad?"
        textoSeguro="Seguir editando" textoPeligroso="Descartar cambios" onSeguro={() => setDialogo(null)} onPeligroso={() => { setDialogo(null); setEditando(false); }} />}
      {dialogo === 'eliminar' && <DialogoConfirmar titulo="Eliminar oportunidad"
        mensaje={`Se eliminará la oportunidad de ${datos.leadNombre || 'este lead'}${datos.sector ? ` (${datos.sector})` : ''}, con fecha ${fechaLarga(datos.fecha_entrada)}. El lead no se borra.\nEsta acción no se puede deshacer.`}
        textoSeguro="No eliminar" textoPeligroso={guardando ? 'Eliminando…' : 'Sí, eliminar'} ocupado={guardando}
        onSeguro={() => setDialogo(null)} onPeligroso={eliminar} />}

      {/* Cabecera */}
      <div style={{ ...S.cabecera, padding: `20px ${pad}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', minWidth: 0 }}>
          <button style={S.botonVolver} onClick={volver}>← {textoVolver}</button>
          <h1 style={{ ...S.titulo, fontSize: isMobile ? '1.25rem' : '1.6rem' }}>{editando ? 'Editar oportunidad' : mostrarLead ? (datos.leadNombre || 'Oportunidad') : `Oportunidad${datos.sector ? ` · ${datos.sector}` : ''}`}</h1>
          <Badge estado={datos.estado} tabla={ESTADOS_OP} />
        </div>
        {!editando && (
          <div style={{ display: 'flex', gap: '8px' }}>
            {esAdmin && !tienePres && presupuestos !== null && <button style={S.botonEliminar} onClick={() => setDialogo('eliminar')}>Eliminar oportunidad</button>}
            <button style={S.botonSecundario} onClick={empezarEdicion}>Editar</button>
          </div>
        )}
      </div>

      <div style={{ padding: `24px ${pad}` }}>
        {error && <p style={S.error}>{error}</p>}

        {!editando ? (
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0,1fr) minmax(0,1fr)', gap: '20px', alignItems: 'start' }}>
            <div style={S.bloque}>
              <h3 style={S.bloqueTitulo}>Datos de la oportunidad</h3>
              {mostrarLead && (
                <Dato label="Lead">
                  {onNavegar
                    ? <button style={S.enlace} onClick={() => onNavegar({ modulo: 'leads', accion: 'ficha', id: datos.leadId })}>{datos.leadNombre || 'Ver lead'}{datos.leadEmpresa ? ` · ${datos.leadEmpresa}` : ''} →</button>
                    : <>{datos.leadNombre}{datos.leadEmpresa ? ` · ${datos.leadEmpresa}` : ''}</>}
                </Dato>
              )}
              <Dato label="Fecha de contacto">{fechaLarga(datos.fecha_entrada)}</Dato>
              <Dato label="Sector">{datos.sector || '—'}</Dato>
              <Dato label="Superficie estimada">{fmtM2(datos.superficie)}</Dato>
              <Dato label="Presupuesto estimado">{fmtEuros(datos.presupuesto_estimado)}</Dato>
              <Dato label="Ubicación">{lugar || '—'}</Dato>
              {datos.observaciones && (
                <div style={{ marginTop: '14px' }}>
                  <span style={S.etiqueta}>Observaciones</span>
                  <p style={{ color: '#fff', fontSize: '0.88rem', marginTop: '6px', whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>{datos.observaciones}</p>
                </div>
              )}
            </div>

            <div style={S.bloque}>
              <h3 style={S.bloqueTitulo}>Presupuestos vinculados{tienePres ? ` · ${presupuestos.length}` : ''}</h3>
              {presupuestos === null
                ? <p style={S.nota}>Cargando…</p>
                : !tienePres
                  ? <p style={S.nota}>Sin presupuestos todavía. El estado lo eliges tú al editar: Pendiente o Descartado.</p>
                  : <>
                    {presupuestos.map(p => (
                      <div key={p.id} style={{ ...S.filaPres, cursor: onNavegar ? 'pointer' : 'default' }}
                        onClick={() => onNavegar && onNavegar({ modulo: 'presupuestos', accion: 'ficha', id: p.id })}>
                        <div style={{ minWidth: 0 }}>
                          <span style={{ color: '#fff', fontSize: '0.88rem', fontWeight: 500 }}>{p.num_presupuesto || 'Sin número'}</span>
                          <span style={{ display: 'block', color: '#AFA9EC', fontSize: '0.75rem' }}>{fechaLarga(p.fecha_presupuesto)} · {fmtEuros(p.importe)}</span>
                        </div>
                        <Badge estado={p.estado} tabla={ESTADOS_PRES} />
                      </div>
                    ))}
                    <p style={{ ...S.nota, marginTop: '12px' }}>El estado de la oportunidad se calcula a partir de estos presupuestos.</p>
                  </>}
            </div>
          </div>
        ) : (
          <div style={S.bloque}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
              <Campo label="Fecha de contacto">
                <input type="date" className="metron-date" style={{ ...S.input, colorScheme: 'dark' }} value={form.fecha_entrada} onChange={e => set('fecha_entrada', e.target.value)} />
              </Campo>
              <Campo label="Estado">
                {estadoManual
                  ? <select style={{ ...S.input, background: '#1a1440' }} value={form.estado} onChange={e => set('estado', e.target.value)}>
                      {OPCIONES_ESTADO_MANUAL.map(o => <option key={o.valor} value={o.valor}>{o.etiqueta}</option>)}
                    </select>
                  : <div style={{ ...S.input, display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Badge estado={datos.estado} tabla={ESTADOS_OP} />
                      <span style={{ color: '#AFA9EC', fontSize: '0.75rem' }}>calculado de sus presupuestos</span>
                    </div>}
              </Campo>
              <Campo label="Sector">
                <select style={{ ...S.input, background: '#1a1440' }} value={form.sector} onChange={e => set('sector', e.target.value)}>
                  <option value="">Sin sector</option>
                  {sectores.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </Campo>
              <Campo label="Superficie estimada (m²)">
                <input type="number" style={{ ...S.input, colorScheme: 'dark' }} value={form.superficie} onChange={e => set('superficie', e.target.value)} placeholder="0" />
              </Campo>
              <Campo label="Presupuesto estimado (€)">
                <input type="number" style={{ ...S.input, colorScheme: 'dark' }} value={form.presupuesto_estimado} onChange={e => set('presupuesto_estimado', e.target.value)} placeholder="0" />
              </Campo>
              <Campo label="Dirección del proyecto" ancho>
                <input style={S.input} value={form.direccion} onChange={e => set('direccion', e.target.value)} placeholder="Calle y número" />
              </Campo>
              <Campo label="Código postal"><input style={S.input} value={form.cp} onChange={e => set('cp', e.target.value)} placeholder="00000" /></Campo>
              <Campo label="Ciudad"><input style={S.input} value={form.ciudad} onChange={e => set('ciudad', e.target.value)} placeholder="Ciudad" /></Campo>
              <Campo label="Provincia"><input style={S.input} value={form.provincia} onChange={e => set('provincia', e.target.value)} placeholder="Provincia" /></Campo>
              <Campo label="País"><input style={S.input} value={form.pais} onChange={e => set('pais', e.target.value)} /></Campo>
              <Campo label="Observaciones" ancho>
                <textarea style={{ ...S.input, resize: 'vertical', minHeight: '100px' }} value={form.observaciones} onChange={e => set('observaciones', e.target.value)} placeholder="Detalles del posible proyecto..." />
              </Campo>
            </div>
          </div>
        )}
      </div>

      {editando && (
        <div style={{ ...S.barraGuardar, padding: `16px ${pad}` }}>
          <button style={S.botonSecundario} onClick={cancelar}>Cancelar</button>
          <button style={S.botonPrimario} onClick={guardar} disabled={guardando}>{guardando ? 'Guardando...' : 'Guardar cambios'}</button>
        </div>
      )}
    </div>
  );
}

const S = {
  cabecera: { position: 'sticky', top: 0, zIndex: 50, background: '#0e0b24', borderBottom: '1px solid rgba(83,74,183,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' },
  titulo: { color: '#ffffff', fontWeight: '300', letterSpacing: '0.02em', margin: 0 },
  botonVolver: { background: 'transparent', color: '#7B6FE8', border: 'none', fontSize: '0.88rem', cursor: 'pointer', padding: 0, fontFamily: 'DM Sans, sans-serif' },
  botonPrimario: { background: '#534AB7', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 18px', fontSize: '0.88rem', fontWeight: '500', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  botonSecundario: { background: 'transparent', color: '#AFA9EC', border: '1px solid rgba(83,74,183,0.4)', borderRadius: '8px', padding: '10px 18px', fontSize: '0.88rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  botonEliminar: { background: 'transparent', color: '#D85A30', border: '1px solid #D85A30', borderRadius: '8px', padding: '10px 18px', fontSize: '0.88rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  enlace: { background: 'transparent', border: 'none', color: '#7B6FE8', fontSize: '0.88rem', cursor: 'pointer', padding: 0, textAlign: 'right', fontFamily: 'DM Sans, sans-serif' },
  bloque: { background: '#1a1440', borderRadius: '12px', padding: '20px 24px', border: '1px solid rgba(83,74,183,0.2)' },
  bloqueTitulo: { color: '#AFA9EC', fontSize: '0.78rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '14px' },
  fila: { display: 'flex', justifyContent: 'space-between', gap: '16px', padding: '7px 0', borderBottom: '1px solid rgba(83,74,183,0.08)' },
  etiqueta: { color: '#AFA9EC', fontSize: '0.85rem', flexShrink: 0 },
  valor: { color: '#ffffff', fontSize: '0.88rem', textAlign: 'right', minWidth: 0, overflowWrap: 'anywhere' },
  filaPres: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', padding: '10px 8px', borderBottom: '1px solid rgba(83,74,183,0.1)', borderRadius: '6px' },
  nota: { color: '#AFA9EC', fontSize: '0.8rem', lineHeight: 1.4 },
  error: { color: '#D85A30', fontSize: '0.85rem', marginBottom: '16px' },
  labelCampo: { color: '#AFA9EC', fontSize: '0.78rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.04em' },
  input: { background: '#0e0b24', border: '1px solid rgba(83,74,183,0.3)', borderRadius: '8px', padding: '10px 14px', color: '#fff', fontSize: '0.88rem', outline: 'none', width: '100%', fontFamily: 'DM Sans, sans-serif', boxSizing: 'border-box' },
  barraGuardar: { position: 'fixed', bottom: 0, left: 0, right: 0, background: '#1a1440', borderTop: '1px solid rgba(83,74,183,0.3)', display: 'flex', justifyContent: 'flex-end', gap: '12px', zIndex: 100 },
};
